/**
 * ISSUE-1362. The quick actions for one chat in the Messages list, the web
 * stand-in for v1's swipe actions on `chat_tile.dart`: Mark as read, and
 * Mute or Unmute. Archive is dropped (messaging overview rule 30).
 *
 * Mark as read calls `/chat/mark-read`, whose generated hook already
 * refreshes `/chat/list-mine`. Unmute calls notification-api's
 * `/chat-mute/clear` and refreshes the same reads the mute sheet does.
 * Mute hands off to the caller, which opens the shared `MuteDialog` with its
 * six durations instead of a second copy of that list here.
 *
 * Each action shows its busy state on its own row only
 * (`scoped-loading-state.mdc`), and every failure goes through the shared
 * error modal.
 */
import { useChatMarkRead } from "@sportsmet/messaging-sdk/private";
import { useChatMuteClear } from "@sportsmet/notification-sdk/private";
import { Button, Dialog } from "@sportsmet/ui";
import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import { refreshChatMuteReads } from "../chatDetails/MuteDialog";
import {
  CHAT_ROW_MARK_READ_LABEL,
  CHAT_ROW_MUTE_LABEL,
  CHAT_ROW_OPEN_LABEL,
  CHAT_ROW_UNMUTE_LABEL,
} from "./chatListActionsCopy";

/** The `?dialog=` value that opens this dialog on `/messages`. */
export const CHAT_ROW_ACTIONS_DIALOG = "chat-actions";
/** The `?dialog=` value for the mute durations opened from a list row. */
export const CHAT_ROW_MUTE_DIALOG = "chat-mute";

export type typeChatRowActionsTarget = {
  chatId: string;
  title: string;
  hasUnread: boolean;
  isMuted: boolean;
};

export function ChatRowActionsDialog({
  open,
  target,
  onOpenChange,
  onOpenChat,
  onChooseMute,
}: {
  open: boolean;
  /** Null while the list is still loading the row the URL names. */
  target: typeChatRowActionsTarget | null;
  onOpenChange: (open: boolean) => void;
  onOpenChat: (chatId: string) => void;
  onChooseMute: (chatId: string) => void;
}): ReactNode {
  const markRead = useChatMarkRead();
  const muteClear = useChatMuteClear();
  const queryClient = useQueryClient();

  const handleMarkRead = async (chatId: string): Promise<void> => {
    try {
      const result = await markRead.mutateAsync({ chatId });
      if (showApiError(result)) return;
      onOpenChange(false);
    } catch {
      showOfflineError();
    }
  };

  const handleUnmute = async (chatId: string): Promise<void> => {
    try {
      const result = await muteClear.mutateAsync({ chatId });
      if (showApiError(result)) return;
      await refreshChatMuteReads(queryClient);
      onOpenChange(false);
    } catch {
      showOfflineError();
    }
  };

  const isBusy = markRead.isPending || muteClear.isPending;

  return (
    <Dialog
      open={open && target !== null}
      onOpenChange={onOpenChange}
      title={target?.title ?? ""}
      autoFocus={false}
    >
      {target !== null ? (
        <div className="flex flex-col gap-2" data-testid="chat-row-actions">
          <Button
            variant="secondary"
            fullWidth
            data-testid="chat-row-action-open"
            disabled={isBusy}
            onClick={() => {
              onOpenChat(target.chatId);
            }}
          >
            {CHAT_ROW_OPEN_LABEL}
          </Button>
          {target.hasUnread ? (
            <Button
              variant="secondary"
              fullWidth
              data-testid="chat-row-action-mark-read"
              loading={markRead.isPending}
              disabled={isBusy}
              onClick={() => {
                void handleMarkRead(target.chatId);
              }}
            >
              {CHAT_ROW_MARK_READ_LABEL}
            </Button>
          ) : null}
          {target.isMuted ? (
            <Button
              variant="secondary"
              fullWidth
              data-testid="chat-row-action-unmute"
              loading={muteClear.isPending}
              disabled={isBusy}
              onClick={() => {
                void handleUnmute(target.chatId);
              }}
            >
              {CHAT_ROW_UNMUTE_LABEL}
            </Button>
          ) : (
            <Button
              variant="secondary"
              fullWidth
              data-testid="chat-row-action-mute"
              disabled={isBusy}
              onClick={() => {
                onChooseMute(target.chatId);
              }}
            >
              {CHAT_ROW_MUTE_LABEL}
            </Button>
          )}
        </div>
      ) : null}
    </Dialog>
  );
}
